import { baseURL } from "../../../../../../../api/dbAxios";
import { IMessage } from "../interfaces/Message";
import { IOutputAdjustment } from "../interfaces/OutputAdjustment";
import { checkAuthentication } from "../../../../../../../auth/AuthGuard";
import {
  ChatLimitReachedError,
  setChatRequestsLeft,
  updateChatRequestsLeftFromResponse,
} from "../../../../../../../helpers/chatRequestsLeft";

export const getChatResponseData = async (
  regulator: string | undefined,
  id: string | undefined,
  messages: IMessage[],
  outputAdj: IOutputAdjustment,
  chatType: "details" | "search",
  onChunk: (chunk: string) => void,
  onFullResponse: (summary: string) => void,
) => {
  const API_AUTH_HEADER = process.env.REACT_APP_API_AUTH_HEADER;
  const isLocalhost = window.location.hostname === "localhost";

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (isLocalhost && API_AUTH_HEADER) {
    headers["Authorization"] = API_AUTH_HEADER;
  }

  const body: any = {
    use_ids: [{ case_no: id, code: regulator }],
    conversation: messages,
    chat_type: chatType,
    details_direct: true,
    stream: true,
  };

  Object.entries(outputAdj).forEach(([key, value]) => {
    if (value !== null) body[key] = value;
  });

  const response = await fetch(`${baseURL}/chat/`, {
    method: "POST",
    headers,
    credentials: isLocalhost ? "same-origin" : "include",
    body: JSON.stringify(body),
  });

  if (response?.status === 401 || response?.status === 403) {
    localStorage.setItem("details_chatAI_backup", JSON.stringify({ messages }));

    await checkAuthentication(undefined, { redirectOn401: true });
  }

  if (!response.ok) {
    throw new Error(`Chat request failed with status ${response.status}`);
  }

  updateChatRequestsLeftFromResponse(response);

  // a blocked request returns plain JSON instead of an event stream
  if (response.headers.get("Content-Type")?.includes("application/json")) {
    setChatRequestsLeft(0);
    throw new ChatLimitReachedError();
  }

  try {
    if (!response.body) throw new Error("Readable stream not supported");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let fullText = "";

    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        const hasResponseMarker = /--RESPONSE--/i.test(fullText);
        if (!hasResponseMarker) {
          throw new Error("Stream ended without --RESPONSE--");
        }

        const realAnswer = fullText
          .split(/--RESPONSE--/i)
          .pop()!
          .trim();

        onFullResponse(realAnswer);
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      fullText += chunk;

      const cleanedChunk = chunk.replace(/--KEEP-ALIVE--/g, "");
      onChunk(cleanedChunk);
    }
  } catch (err) {
    console.error("Stream error:", err);
    throw err;
  }
};
